import { createReducer, on } from '@ngrx/store';
import { logoutSuccess } from 'src/app/core/store/user/user.actions';
import { isPackingListUnavailableError } from './packing-lists-error.utils';
import * as PackingListsActions from './packing-lists.actions';
import { initialState } from './packing-lists.state';

const upsertPackingList = <T extends { id: string }>(
  currentItems: T[],
  nextItem: T
): T[] => {
  const existingIndex = currentItems.findIndex((item) => item.id === nextItem.id);

  if (existingIndex === -1) {
    return [...currentItems, nextItem];
  }

  return currentItems.map((item) => (item.id === nextItem.id ? nextItem : item));
};

const finishSaving = (state: typeof initialState) => ({
  ...state,
  saving: false,
});

const setLoadError = (state: typeof initialState, error: string) => ({
  ...state,
  saving: false,
  loadError: error,
  addItemError: null,
});

const setAddItemError = (state: typeof initialState, error: string) => ({
  ...state,
  saving: false,
  addItemError: error,
});

export const packingListsReducer = createReducer(
  initialState,

  on(logoutSuccess, () => initialState),

  on(PackingListsActions.loadPackingLists, (state) => ({
    ...state,
    loading: true,
    loadError: null,
    addItemError: null,
  })),

  on(PackingListsActions.loadPackingListsSuccess, (state, { packingLists }) => ({
    ...state,
    lists: packingLists,
    loading: false,
    loadError: null,
  })),

  on(PackingListsActions.loadPackingListsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loadError: error,
  })),

  on(PackingListsActions.loadPackingList, (state) => ({
    ...state,
    loading: true,
    loadError: null,
    addItemError: null,
  })),

  on(PackingListsActions.loadPackingListSuccess, (state, { packingList }) => ({
    ...state,
    lists: upsertPackingList(state.lists, packingList),
    loading: false,
    loadError: null,
  })),

  on(PackingListsActions.loadPackingListFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loadError: error,
  })),

  on(PackingListsActions.addPackingListItem, (state) => ({
    ...state,
    saving: true,
    addItemError: null,
  })),

  on(
    PackingListsActions.createPackingList,
    PackingListsActions.renamePackingList,
    PackingListsActions.deletePackingList,
    PackingListsActions.togglePackingListItem,
    PackingListsActions.renamePackingListItem,
    PackingListsActions.deletePackingListItem,
    PackingListsActions.bulkDeletePackingListItems,
    PackingListsActions.reorderPackingListItems,
    (state) => ({
      ...state,
      saving: true,
    })
  ),

  on(PackingListsActions.createPackingListSuccess, (state, { packingList }) => ({
    ...state,
    lists: [packingList, ...state.lists],
    saving: false,
  })),

  on(PackingListsActions.deletePackingListSuccess, (state, { listId }) => ({
    ...state,
    lists: state.lists.filter((list) => list.id !== listId),
    saving: false,
  })),

  on(PackingListsActions.addPackingListItemSuccess, (state, { packingList }) => ({
    ...state,
    lists: upsertPackingList(state.lists, packingList),
    saving: false,
    addItemError: null,
  })),

  on(
    PackingListsActions.renamePackingListSuccess,
    PackingListsActions.togglePackingListItemSuccess,
    PackingListsActions.renamePackingListItemSuccess,
    PackingListsActions.deletePackingListItemSuccess,
    PackingListsActions.bulkDeletePackingListItemsSuccess,
    PackingListsActions.reorderPackingListItemsSuccess,
    (state, { packingList }) => ({
      ...state,
      lists: upsertPackingList(state.lists, packingList),
      saving: false,
    })
  ),

  on(PackingListsActions.createPackingListFailure, (state) => finishSaving(state)),

  on(PackingListsActions.addPackingListItemFailure, (state, { error }) =>
    isPackingListUnavailableError(error)
      ? setLoadError(state, error)
      : setAddItemError(state, error)
  ),

  on(PackingListsActions.renamePackingListFailure, (state, { error }) =>
    isPackingListUnavailableError(error) ? setLoadError(state, error) : finishSaving(state)
  ),

  on(PackingListsActions.deletePackingListFailure, (state, { error }) =>
    isPackingListUnavailableError(error) ? setLoadError(state, error) : finishSaving(state)
  ),

  on(PackingListsActions.togglePackingListItemFailure, (state, { error }) =>
    isPackingListUnavailableError(error) ? setLoadError(state, error) : finishSaving(state)
  ),

  on(PackingListsActions.renamePackingListItemFailure, (state, { error }) =>
    isPackingListUnavailableError(error) ? setLoadError(state, error) : finishSaving(state)
  ),

  on(PackingListsActions.deletePackingListItemFailure, (state, { error }) =>
    isPackingListUnavailableError(error) ? setLoadError(state, error) : finishSaving(state)
  ),

  on(PackingListsActions.bulkDeletePackingListItemsFailure, (state, { error }) =>
    isPackingListUnavailableError(error) ? setLoadError(state, error) : finishSaving(state)
  ),

  on(PackingListsActions.reorderPackingListItemsFailure, (state, { error }) =>
    isPackingListUnavailableError(error) ? setLoadError(state, error) : finishSaving(state)
  )
);
